// L'anneau de score — la « bulle » ronde du widget, devenue partageable.
//
// POURQUOI ELLE MONTE DANS LE PAQUET. Elle était écrite en dur dans le shadow
// DOM de l'extension (`content/index.tsx`), et la console n'avait donc que sa
// pastille rectangulaire : deux représentations du MÊME chiffre dans le même
// produit. Recopier le CSS côté console aurait rouvert exactement la
// divergence que `primitivesCss` a fermée pour les boutons.
//
// La géométrie est en littéraux (règle du paquet : les `--r-*` de la page hôte
// n'existent pas dans un shadow-root). Les couleurs viennent toutes de `--s-*`.

import { useState, type CSSProperties } from 'react'
import { mouvementReduit, useCompteur } from './mouvement'
import type { ScoreTone } from './scoreTone'

export interface ScoreRingProps {
  /** 0..100 — l'arc plein. */
  score: number
  /**
   * Score atteignable après adaptation du CV. Dessiné en arc CLAIR au-delà de
   * l'arc plein : la marge se voit sans qu'aucun chiffre ne la commente.
   * Ignoré s'il ne dépasse pas `score` (il n'y a alors rien à gagner).
   */
  potential?: number
  /**
   * Palier de couleur. EXPLICITE et non déduit ici : les deux apps utilisent
   * `scoreTone` du paquet, et un défaut silencieux ferait passer inaperçue une
   * app qui aurait gardé ses propres seuils.
   */
  tone: ScoreTone
  /** Diamètre en px : 88 pour un héros, 64 en comparaison, 34 en liste. */
  size?: number
  /** Le « /100 » sous le chiffre. Coupé sous 56 px, où il devient illisible. */
  showMax?: boolean
  /**
   * Le palier écrit en toutes lettres sous l'anneau (« Bonne correspondance »,
   * « À vérifier »...). Fourni par l'app, jamais déduit ici : le paquet ne
   * connaît pas la langue de l'appelant (l'extension traduit, la console ne
   * parle que français), et un mot posé en dur romprait la même règle que
   * `tone` explicite juste au-dessus. Absent par défaut : les usages existants
   * (widget compact, comparateur) gardent leur rendu, seuls les écrans qui
   * demandent explicitement ce mot l'affichent.
   */
  toneLabel?: string
  /** Libellé lu par les lecteurs d'écran — le SVG, lui, est décoratif. */
  ariaLabel?: string
  testId?: string
  className?: string
  /**
   * L'arc monte de zéro, le chiffre compte, puis la marge se dessine et le mot
   * de ton se pose (chantier « Mouvement », issue #183). C'est l'appelant qui
   * sait si c'est la première fois qu'on voit cette offre : l'anneau ne garde
   * aucune mémoire. `false` par défaut ; rien ne bouge sous « réduire les
   * animations ».
   */
  anime?: boolean
}

/** Rayon du tracé dans le viewBox. Fixe : c'est `size` qui met à l'échelle. */
const R = 46
const C = 2 * Math.PI * R

/** Un anneau fin sur 88 px devient invisible sur 34 : l'épaisseur suit. */
function stroke(size: number): number {
  return size >= 72 ? 9 : size >= 56 ? 10 : 13
}

export function ScoreRing({
  score,
  potential,
  tone,
  size = 88,
  showMax,
  toneLabel,
  ariaLabel,
  testId,
  className,
  anime = false,
}: ScoreRingProps) {
  const arc = (v: number) => `${(Math.max(0, Math.min(100, v)) / 100) * C} ${C}`
  const gain = potential !== undefined && potential > score
  const w = stroke(size)
  const max = showMax ?? size >= 56
  // Décidé au montage : un anneau déjà affiché ne repart pas de zéro, et
  // matchMedia n'est pas interrogé à chaque rendu d'une longue liste.
  const [joue] = useState(() => anime && !mouvementReduit())
  const affiche = useCompteur(score, joue)

  return (
    <div
      className={`score-ring-wrap${toneLabel ? ' with-tone' : ''}${joue ? ' anime' : ''}`}
      data-testid={testId ? `${testId}-wrap` : undefined}
    >
    <div
      className={`score-ring${joue ? ' anime' : ''}${className ? ` ${className}` : ''}`}
      style={{ '--ring-size': `${size}px`, '--arc-score': arc(score) } as CSSProperties}
      data-testid={testId}
      role="img"
      aria-label={ariaLabel ?? (toneLabel ? `${score} sur 100, ${toneLabel.toLowerCase()}` : `${score} sur 100`)}
    >
      <svg viewBox="0 0 104 104" width={size} height={size} aria-hidden="true">
        <circle className="ring-track" cx="52" cy="52" r={R} fill="none" strokeWidth={w} />
        {gain && (
          <circle
            className={`ring-head ${tone}`}
            cx="52"
            cy="52"
            r={R}
            fill="none"
            strokeWidth={w}
            strokeLinecap="round"
            strokeDasharray={arc(potential!)}
          />
        )}
        <circle
          className={`ring-cur ${tone}`}
          cx="52"
          cy="52"
          r={R}
          fill="none"
          strokeWidth={w}
          strokeLinecap="round"
          strokeDasharray={arc(score)}
        />
      </svg>
      <div className="score-ring-center">
        <span className={`score-ring-num ${tone}`}>{affiche}</span>
        {max && <span className="score-ring-max">/100</span>}
      </div>
    </div>
    {toneLabel && <span className={`score-ring-tone ${tone}`}>{toneLabel}</span>}
    </div>
  )
}
